import React, { useState, useEffect } from 'react';
import HelpDocumentation from './HelpDocumentation';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function TeamScoreEntry() {
    const [events, setEvents] = useState([]);
    const [selectedEventID, setSelectedEventID] = useState('');
    const [competitions, setCompetitions] = useState([]);
    const [selectedCompetitionID, setSelectedCompetitionID] = useState('');
    const [selectedCompetition, setSelectedCompetition] = useState(null);
    const [teams, setTeams] = useState([]);
    const [scoringComponents, setScoringComponents] = useState([]);
    const [teamScores, setTeamScores] = useState({});  // Object to store scores for each team
    const [teamMembers, setTeamMembers] = useState({}); // Object to store team members for each team
    const [expandedTeams, setExpandedTeams] = useState({}); // Track which teams are showing members
    const [editableRow, setEditableRow] = useState(null);
    const [originalTeamScores, setOriginalTeamScores] = useState({});
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editedTeamID, setEditedTeamID] = useState(null);
    const [editedAttemptNumber, setEditedAttemptNumber] = useState(1);
    const [tempTeamScores, setTempTeamScores] = useState([]);
    
    // Add loading state variables
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingCompetitions, setLoadingCompetitions] = useState(false);
    const [loadingTeamData, setLoadingTeamData] = useState(false);
    const [refreshingScores, setRefreshingScores] = useState(false);
    const [loadingTeamScores, setLoadingTeamScores] = useState({});

    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

    const openHelpModal = () => setIsHelpModalOpen(true);
    const closeHelpModal = () => setIsHelpModalOpen(false);

    useEffect(() => {
        const fetchEvents = async () => {
            setLoadingEvents(true);
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                const enabledEvents = Array.isArray(data) ? data.filter(event => event.Enabled) : [];
                setEvents(enabledEvents);
                if (enabledEvents.length > 0) {
                    setSelectedEventID(enabledEvents[0].EventID); // Auto-select first enabled event
                }
            } catch (error) {
                console.error('Error fetching events:', error);
            } finally {
                setLoadingEvents(false);
            }
        };
        fetchEvents();
    }, []);

    useEffect(() => {
        if (!selectedEventID) return;
        setSelectedCompetitionID(''); // Reset competition selection
    }, [selectedEventID]);


    useEffect(() => {
        if (!selectedEventID) return;
        setSelectedCompetitionID(''); // Reset competition selection
        const fetchCompetitions = async () => {
            setLoadingCompetitions(true);
            try {
                const response = await fetch(`${API_BASE_URL}/getCompetitions?EventID=${selectedEventID}`);
                const data = await response.json();
                setCompetitions(Array.isArray(data) ? data.filter(comp => comp.TeamBased) : []); // Filter for team-based competitions
            } catch (error) {
                console.error('Error fetching competitions:', error);
            } finally {
                setLoadingCompetitions(false);
            }
        };
        fetchCompetitions();
    }, [selectedEventID]);

    useEffect(() => {
        if (selectedCompetitionID) {
            // Get the selected competition details
            const competition = competitions.find(comp => comp.CompetitionID.toString() === selectedCompetitionID.toString());
            setSelectedCompetition(competition);
            
            setLoadingTeamData(true);
            fetchTeams(selectedCompetitionID)
                .then(() => fetchScoringComponents(selectedCompetitionID))
                .finally(() => {
                    setLoadingTeamData(false);
                });
        }
    }, [selectedCompetitionID]);

    const fetchTeams = async (competitionID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getTeams?CompetitionID=${competitionID}`);
            const data = await response.json();
            const teamsData = Array.isArray(data) ? data : [];
            setTeams(teamsData);
            
            // Initialize team scores and fetch team members
            const scorePromises = [];
            const memberPromises = [];
            
            for (const team of teamsData) {
                scorePromises.push(fetchTeamScores(team.TeamID));
                memberPromises.push(fetchTeamMembers(team.TeamID));
            }
            
            await Promise.all([...scorePromises, ...memberPromises]);
            return teamsData;
        } catch (error) {
            console.error('Error fetching teams:', error);
        }
    };

    const fetchScoringComponents = async (competitionID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getScoringComponents?CompetitionID=${competitionID}`);
            const data = await response.json();
            setScoringComponents(Array.isArray(data) ? data : []);
            return data;
        } catch (error) {
            console.error('Error fetching scoring components:', error);
        }
    };

    const fetchTeamScores = async (teamID) => {
        setLoadingTeamScores(prev => ({ ...prev, [teamID]: true }));
        try {
            const response = await fetch(`${API_BASE_URL}/getTeamScores?TeamID=${teamID}`);
            const data = await response.json();
            setTeamScores(prevScores => ({
                ...prevScores,
                [teamID]: Array.isArray(data) ? data : []
            }));
            return data;
        } catch (error) {
            console.error('Error fetching scores:', error);
        } finally {
            setLoadingTeamScores(prev => ({ ...prev, [teamID]: false }));
        }
    };

    const fetchTeamMembers = async (teamID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getTeamMembers?TeamID=${teamID}`);
            const data = await response.json();
            setTeamMembers(prevMembers => ({
                ...prevMembers,
                [teamID]: Array.isArray(data) ? data : []
            }));
            return data;
        } catch (error) {
            console.error('Error fetching team members:', error);
        }
    };

    const handleScoreChange = (teamID, scoringComponentID, value, componentType, attemptNumber = 1) => {
        setTeamScores(prevTeamScores => {
            const teamScoreArray = [...(prevTeamScores[teamID] || [])];
            const scoreIndex = teamScoreArray.findIndex(score => 
                score.ScoringComponentID === scoringComponentID &&
                score.AttemptNumber === attemptNumber
            );
            
            // For INT type, ensure the value is an integer
            if (componentType === 'INT' && value !== '') {
                value = Math.floor(Number(value));
            }
            // For DECIMAL type, preserve the decimal values but ensure it's a number
            else if (componentType === 'DECIMAL' && value !== '') {
                value = Number(value);
            }
            
            if (scoreIndex > -1) {
                teamScoreArray[scoreIndex] = {
                    ...teamScoreArray[scoreIndex],
                    Multiplier: value
                };
            } else {
                teamScoreArray.push({
                    ScoringComponentID: scoringComponentID,
                    Multiplier: value,
                    EventID: selectedEventID,
                    CompetitionID: selectedCompetitionID,
                    AttemptNumber: attemptNumber
                });
            }
            
            return {
                ...prevTeamScores,
                [teamID]: teamScoreArray
            };
        });
    };

    const handleIncrementDecrement = (teamID, scoringComponentID, componentType, increment, attemptNumber = 1) => {
        const teamScore = teamScores[teamID] || [];
        const currentScore = teamScore.find(score => 
            score.ScoringComponentID === scoringComponentID && 
            score.AttemptNumber === attemptNumber
        );
        
        let currentValue = currentScore ? Number(currentScore.Multiplier) || 0 : 0;
        let newValue;
        
        // For DECIMAL type, increment/decrement by 0.25 or another appropriate fraction
        if (componentType === 'DECIMAL') {
            newValue = currentValue + (increment * 0.25); // Increment/decrement by 0.25
        } else {
            newValue = currentValue + increment;
            
            // Ensure integer values for INT type
            if (componentType === 'INT') {
                newValue = Math.floor(newValue);
            }
        }
        
        // Prevent negative values if needed
        if (newValue < 0) newValue = 0;
        
        handleScoreChange(teamID, scoringComponentID, newValue, componentType, attemptNumber);
    };

    const handleBooleanChange = (teamID, scoringComponentID, checked, attemptNumber = 1) => {
        handleScoreChange(teamID, scoringComponentID, checked ? 1 : 0, 'BOOL', attemptNumber);
    };

    const handleDeleteScore = async (teamID, attemptNumber = 1) => {
        const confirmDelete = window.confirm(`!!!Warning!!! Are you sure you want to delete the scores for attempt ${attemptNumber}?`);
        if (!confirmDelete) return;
        
        try {
            const response = await fetch(`${API_BASE_URL}/deleteTeamScore?TeamID=${teamID}&CompetitionID=${selectedCompetitionID}&EventID=${selectedEventID}&AttemptNumber=${attemptNumber}`, {
                method: 'DELETE'
            });
            
            if (response.ok) {
                refreshScores(); // refreshes scores from server
                alert('Team scores deleted successfully.');
            } else {
                alert('Error deleting team scores.');
            }
        } catch (error) {
            console.error('Error deleting team scores:', error);
        }
    };

    const handleEditClick = (teamID) => {
        // Save the current team scores before editing
        setOriginalTeamScores(prevOriginalScores => ({
            ...prevOriginalScores,
            [teamID]: [...(teamScores[teamID] || [])]
        }));
        setEditableRow(teamID);
    };

    const handleSaveClick = async (teamID) => {
        const scores = teamScores[teamID] || [];
        const teamScoreData = scoringComponents.map(component => {
            const existingScore = scores.find(score => score.ScoringComponentID === component.ScoringComponentID);
            return {
                TeamID: teamID,
                ScoringComponentID: component.ScoringComponentID,
                Multiplier: existingScore ? existingScore.Multiplier : 0,
                EventID: selectedEventID,
                CompetitionID: selectedCompetitionID
            };
        });

        try {
            const response = await fetch(`${API_BASE_URL}/createOrUpdateTeamScores`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(teamScoreData)
            });
            
            if (response.ok) {
                // Success - keep the updated scores
                alert('Team scores saved successfully.');
                setEditableRow(null);
                // Refresh scores after successful save
                refreshScores();
            } else {
                // Error - restore original scores
                alert('Error submitting team scores. Changes were not saved.');
                setTeamScores(prevScores => ({
                    ...prevScores,
                    [teamID]: [...(originalTeamScores[teamID] || [])]
                }));
                setEditableRow(null);
            }
        } catch (error) {
            console.error('Error submitting scores:', error);
            alert(`Error: ${error.message}. Changes were not saved.`);
            setTeamScores(prevScores => ({
                ...prevScores,
                [teamID]: [...(originalTeamScores[teamID] || [])]
            }));
            setEditableRow(null);
        }
    };

    const handleCancelClick = () => {
        if (editableRow) {
            // Restore original team scores when cancelling
            setTeamScores(prevScores => ({
                ...prevScores,
                [editableRow]: [...(originalTeamScores[editableRow] || [])]
            }));
        }
        setEditableRow(null);
    };

    const toggleTeamExpand = (teamID) => {
        setExpandedTeams(prev => ({
            ...prev,
            [teamID]: !prev[teamID]
        }));
    };

    const refreshScores = () => {
        if (selectedCompetitionID) {
            setRefreshingScores(true);
            fetchTeams(selectedCompetitionID)
                .then(() => fetchScoringComponents(selectedCompetitionID))
                .finally(() => {
                    setRefreshingScores(false);
                });
        }
    };

    const openEditModal = (teamID, attemptNumber = 1) => {
        const team = teams.find(t => t.TeamID === teamID);
        if (!team) return;
        
        // Create scores for this team's specific attempt
        const newTempScores = scoringComponents.map(component => {
            const teamScoreArray = teamScores[teamID] || [];
            const existingScore = teamScoreArray.find(score => 
                score.ScoringComponentID === component.ScoringComponentID && 
                score.AttemptNumber === attemptNumber
            );
            
            return {
                TeamID: teamID,
                ScoringComponentID: component.ScoringComponentID,
                Multiplier: existingScore ? existingScore.Multiplier : (component.ScoringComponentType === 'BOOL' ? 0 : 0),
                EventID: selectedEventID,
                CompetitionID: selectedCompetitionID,
                AttemptNumber: attemptNumber,
                ScoringComponentType: component.ScoringComponentType // Include component type for validation
            };
        });
        
        setTempTeamScores(newTempScores);
        setEditedTeamID(teamID);
        setEditedAttemptNumber(attemptNumber);
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditedTeamID(null);
        setTempTeamScores([]);
    };

    const getScore = (scoringComponentID) => {
        const score = tempTeamScores.find(s => s.ScoringComponentID === scoringComponentID);
        return score ? score.Multiplier : '';
    };

    const handleTempScoreChange = (scoringComponentID, value, componentType) => {
        // Prevent negative values and handle invalid input
        if (value === '' || value === '-' || isNaN(Number(value))) value = 0;
        if (Number(value) < 0) value = 0;
        setTempTeamScores(prevScores => {
            const updatedScores = [...prevScores];
            const scoreIndex = updatedScores.findIndex(score => 
                score.ScoringComponentID === scoringComponentID
            );
            
            // Format value based on component type
            if (componentType === 'INT' && value !== '') {
                value = Math.floor(Number(value));
            } else if (componentType === 'DECIMAL' && value !== '') {
                value = Number(value);
            }
            
            if (scoreIndex > -1) {
                updatedScores[scoreIndex] = {
                    ...updatedScores[scoreIndex],
                    Multiplier: value
                };
            }
            
            return updatedScores;
        });
    };

    const handleTempBooleanChange = (scoringComponentID, checked) => {
        handleTempScoreChange(scoringComponentID, checked ? 1 : 0, 'BOOL');
    };

    const handleTempIncrementDecrement = (scoringComponentID, componentType, increment) => {
        const score = tempTeamScores.find(s => s.ScoringComponentID === scoringComponentID);
        let currentValue = score ? Number(score.Multiplier) || 0 : 0;
        let newValue;
        
        if (componentType === 'DECIMAL') {
            newValue = currentValue + (increment * 0.25);
        } else {
            newValue = currentValue + increment;
            if (componentType === 'INT') {
                newValue = Math.floor(newValue);
            }
        }
        
        if (newValue < 0) newValue = 0;
        
        handleTempScoreChange(scoringComponentID, newValue, componentType);
    };

    const saveScores = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/createOrUpdateTeamScores`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(tempTeamScores.map(score => ({
                    ...score,
                    Multiplier: score.Multiplier === null ? 0 : score.Multiplier
                })))
            });
            
            if (response.ok) {
                // Update the local teamScores state with the new data
                setTeamScores(prev => ({
                    ...prev,
                    [editedTeamID]: tempTeamScores
                }));
                closeEditModal();
                // Optional: refresh scores from server
                refreshScores();
            } else {
                alert('Error submitting team scores. Changes were not saved.');
            }
        } catch (error) {
            console.error('Error submitting scores:', error);
            alert(`Error: ${error.message}. Changes were not saved.`);
        }
    };

    // Generate rows for all teams with their attempts
    const generateTeamRows = () => {
        if (!teams || !selectedCompetition) return [];
        
        const rows = [];
        const attemptsAllowed = selectedCompetition.AttemptsAllowed || 1;
        
        teams.forEach(team => {
            // Create rows for each allowed attempt
            for (let attemptNum = 1; attemptNum <= attemptsAllowed; attemptNum++) {
                rows.push({
                    ...team,
                    attemptNumber: attemptNum
                });
            }
        });
        
        return rows;
    };

    // Get the score value for display in the table
    const getTeamScoreValue = (teamID, scoringComponentID, attemptNumber) => {
        const teamScore = teamScores[teamID] || [];
        const score = teamScore.find(score => 
            score.ScoringComponentID === scoringComponentID && 
            score.AttemptNumber === attemptNumber
        );
        
        if (!score) return '-'; // Return a placeholder if no score exists
        
        const component = scoringComponents.find(c => c.ScoringComponentID === scoringComponentID);
        if (component && component.ScoringComponentType === 'BOOL') {
            return score.Multiplier ? 'Yes' : 'No';
        }
        
        return score.Multiplier || 0;
    };

    const teamRows = generateTeamRows();
    
    // Loading indicator component
    const LoadingSpinner = () => (
        <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading data...</p>
        </div>
    );

    return (
        <div className="flex flex-column gap-1" style={{ width: '100%' }}>
            <h2>Team Score Entry</h2>

            <div className="flex gap-1 mb-1">
                <label>
                    <select 
                        className="form-select" 
                        value={selectedEventID} 
                        onChange={(e) => setSelectedEventID(e.target.value)}
                        disabled={loadingEvents}
                    >
                        <option value="">
                            {loadingEvents ? "Loading events..." : "Select Event"}
                        </option>
                        {Array.isArray(events) && events.map(event => (
                            <option key={event.EventID} value={event.EventID}>{event.EventName}</option>
                        ))}
                    </select>
                </label>

                <label>
                    <select 
                        className="form-select"
                        value={selectedCompetitionID} 
                        onChange={(e) => setSelectedCompetitionID(e.target.value)} 
                        disabled={!selectedEventID || loadingCompetitions}
                    >
                        <option value="">
                            {loadingCompetitions ? "Loading competitions..." : "Select Competition"}
                        </option>
                        {Array.isArray(competitions) && competitions.map(comp => (
                            <option key={comp.CompetitionID} value={comp.CompetitionID}>{comp.CompetitionName}</option>
                        ))}
                    </select>
                </label>
                
                <button 
                    className="btn btn-secondary" 
                    onClick={refreshScores}
                    disabled={!selectedCompetitionID || refreshingScores || loadingTeamData || editableRow !== null}
                >
                    {refreshingScores ? "Refreshing..." : "Refresh Scores"}
                </button>
                <button 
                    className="btn btn-info" 
                    onClick={openHelpModal}
                >
                    Help
                </button>
            </div>

            {/* Help Modal */}
            {isHelpModalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <HelpDocumentation />
                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={closeHelpModal}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {selectedCompetitionID && (
                <div className="data-table-wrapper">
                    {loadingTeamData ? (
                        <LoadingSpinner />
                    ) : teams.length > 0 && selectedCompetition ? (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Team</th>
                                    <th>Attempt</th>
                                    {Array.isArray(scoringComponents) && scoringComponents.map(component => (
                                        <th key={component.ScoringComponentID} style={{ width: '100px' }}>
                                            {component.ScoringComponentName} ({component.ScoringComponentName === 'Disqualified' ? '∞' : (component.Points > 0 ? '+' : '') + component.Points})
                                        </th>
                                    ))}
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teamRows.map((row, index) => {
                                    const isExpanded = expandedTeams[row.TeamID] || false;
                                    const members = teamMembers[row.TeamID] || [];
                                    const isLastAttemptForTeam = (index + 1) % selectedCompetition.AttemptsAllowed === 0;
                                    const isLoading = loadingTeamScores[row.TeamID];

                                    return (
                                        <React.Fragment key={`${row.TeamID}-${row.attemptNumber}`}>
                                            <tr style={{ 
                                                borderBottom: isLastAttemptForTeam && !isExpanded ? '2px solid #000' : '1px solid #ddd',
                                                backgroundColor: Math.floor(index / selectedCompetition.AttemptsAllowed) % 2 === 0 ? '#f9f9f9' : 'white'
                                            }}>
                                                <td>
                                                    {row.attemptNumber === 1 && (
                                                        <div className="flex items-center">
                                                            <button 
                                                                onClick={() => toggleTeamExpand(row.TeamID)}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    fontSize: '1.2rem',
                                                                    padding: '0 0.5rem 0 0'
                                                                }}
                                                            >
                                                                {isExpanded ? '▼' : '►'}
                                                            </button>
                                                            <strong>{row.TeamName}</strong>
                                                            <span style={{ marginLeft: '0.5rem', color: '#666', fontSize: '0.8rem' }}>
                                                                ({members.length} members)
                                                            </span>
                                                        </div>
                                                    )}
                                                    {row.attemptNumber > 1 && <span style={{ marginLeft: '1.7rem' }}></span>}
                                                </td>
                                                <td style={{ fontWeight: 'bold' }}>{row.attemptNumber}</td>
                                                
                                                {Array.isArray(scoringComponents) && scoringComponents.map(component => (
                                                    <td key={component.ScoringComponentID}>
                                                        {isLoading ? (
                                                            <span className="mini-spinner"></span>
                                                        ) : (
                                                            getTeamScoreValue(row.TeamID, component.ScoringComponentID, row.attemptNumber)
                                                        )}
                                                    </td>
                                                ))}
                                                
                                                <td>
                                                    <button 
                                                        className="btn btn-primary" 
                                                        onClick={() => openEditModal(row.TeamID, row.attemptNumber)}
                                                        disabled={isLoading}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button 
                                                        className="btn btn-danger" 
                                                        onClick={() => handleDeleteScore(row.TeamID, row.attemptNumber)}
                                                        style={{ marginLeft: '0.5rem' }}
                                                        disabled={isLoading}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                            
                                            {isExpanded && isLastAttemptForTeam && (
                                                <tr style={{ borderBottom: '2px solid #000' }}>
                                                    <td colSpan={scoringComponents.length + 3} style={{ padding: 0 }}>
                                                        <div className="card">
                                                            <h4 className="mt-1 mb-1">Team Members</h4>
                                                            <table className="data-table">
                                                                <thead>
                                                                    <tr>
                                                                        <th>ID</th>
                                                                        <th>Name</th>
                                                                        <th>Organization</th>
                                                                        <th>Division</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {members.length > 0 ? (
                                                                        members.map(member => (
                                                                            <tr key={member.CompetitorID}>
                                                                                <td>{member.CompetitorID}</td>
                                                                                <td>{member.FirstName} {member.LastName}</td>
                                                                                <td>{member.Organization}</td>
                                                                                <td>{member.Division}</td>
                                                                            </tr>
                                                                        ))
                                                                    ) : (
                                                                        <tr>
                                                                            <td colSpan={4} style={{ textAlign: 'center' }}>
                                                                                No team members found
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className="empty-state">
                            <p>No teams found for this competition.</p>
                        </div>
                    )}
                </div>
            )}

            {isEditModalOpen && editedTeamID && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>Edit Scores for {teams.find(t => t.TeamID === editedTeamID)?.TeamName} - Attempt {editedAttemptNumber}</h3>
                        <div className="score-form">
                            {scoringComponents.map(component => (
                                <div key={component.ScoringComponentID} className="score-input-group">
                                    <label>{component.ScoringComponentName} ({component.ScoringComponentName === 'Disqualified' ? '∞' : (component.Points > 0 ? '+' : '') + component.Points})</label>
                                    {component.ScoringComponentType === 'BOOL' ? (
                                        <div className="checkbox-wrapper">
                                            <input
                                                type="checkbox"
                                                checked={getScore(component.ScoringComponentID) === 1}
                                                onChange={(e) => handleTempBooleanChange(component.ScoringComponentID, e.target.checked)}
                                            />
                                            <span>Yes</span>
                                        </div>
                                    ) : (
                                        <div className="number-input-wrapper">
                                            <button 
                                                type="button"
                                                onClick={() => handleTempIncrementDecrement(component.ScoringComponentID, component.ScoringComponentType, -1)}
                                            >−</button>
                                            {component.ScoringComponentType === 'DECIMAL' ? (
                                                <input
                                                    type="number"
                                                    className="mobile-number-field"
                                                    inputMode="decimal"
                                                    pattern="[0-9]*[.,]?[0-9]*"
                                                    value={getScore(component.ScoringComponentID)}
                                                    onChange={(e) => handleTempScoreChange(component.ScoringComponentID, e.target.value, component.ScoringComponentType)}
                                                    step="0.25"
                                                    min="0"
                                                    autoComplete="off"
                                                />
                                            ) : (
                                                <input
                                                    type="number"
                                                    className="mobile-number-field"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    value={getScore(component.ScoringComponentID)}
                                                    onChange={(e) => handleTempScoreChange(component.ScoringComponentID, e.target.value, component.ScoringComponentType)}
                                                    step="1"
                                                    min="0"
                                                    autoComplete="off"
                                                />
                                            )}
                                            <button 
                                                type="button"
                                                onClick={() => handleTempIncrementDecrement(component.ScoringComponentID, component.ScoringComponentType, 1)}
                                            >+</button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="modal-actions">
                            <button className="btn btn-primary" onClick={saveScores}>Save</button>
                            <button className="btn btn-secondary" onClick={closeEditModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Add CSS for loading spinner and mini spinner */}
            <style jsx>{`
                .loading-spinner {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 2rem;
                    width: 100%;
                }
                .spinner {
                    border: 4px solid rgba(0, 0, 0, 0.1);
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    border-left-color: #09f;
                    animation: spin 1s linear infinite;
                }
                .mini-spinner {
                    display: inline-block;
                    border: 2px solid rgba(0, 0, 0, 0.1);
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    border-left-color: #09f;
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .empty-state {
                    text-align: center;
                    padding: 2rem;
                    background: #f9f9f9;
                    border-radius: 4px;
                    width: 100%;
                }
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    z-index: 1000;
                }
                .modal {
                    background: white;
                    padding: 1rem;
                    border-radius: 8px;
                    max-width: 600px;
                    width: 90%;
                    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
                }
                .modal-actions {
                    margin-top: 1rem;
                    text-align: right;
                }
            `}</style>
        </div>
    );
}
